<template>
  <div class="min-h-screen bg-fuchsia-50 p-4 md:p-8 pt-20 md:pt-8">
    <div class="container mx-auto">

      <!-- Create New Mandala - Prominent Section -->
      <div class="mb-4 bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 p-6 md:p-8 rounded-2xl shadow-xl">
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-2">{{ t('message.createNewMandala') }}</h2>
        <p class="text-purple-100 mb-4 text-sm md:text-base">{{ t('message.wishPhraseDesc') }}</p>
        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <input
            type="text"
            v-model="phrase"
            class="flex-1 border-0 p-4 rounded-xl text-lg shadow-inner focus:ring-4 focus:ring-purple-300 focus:outline-none pointer-events-auto z-10"
            :placeholder="t('message.enterYourPhrase')"
          >
          <button
            @click="drawMandala"
            class="bg-white text-purple-600 px-6 md:px-8 py-4 rounded-xl text-lg font-bold hover:bg-purple-50 hover:scale-105 transition-all duration-200 shadow-lg whitespace-nowrap">
            {{ t('message.go') }}
          </button>
        </div>
      </div>

      <!-- Scroll indicator for previously created mandalas -->
      <div class="flex flex-col items-center justify-center mb-6 text-purple-600">
        <p class="text-sm font-medium mb-2">Или используйте ранее созданные</p>
        <div class="animate-bounce">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </div>

      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-bold text-gray-800">{{ t('message.yourMandalas') }}</h1>
          <button
            @click="togglePhrasesVisibility"
            class="p-2 rounded-full hover:bg-purple-100 transition-colors"
            :title="phrasesHidden ? t('message.showPhrases') : t('message.hidePhrases')"
          >
            <EyeSlashIcon v-if="phrasesHidden" class="h-6 w-6 text-gray-500" />
            <EyeIcon v-else class="h-6 w-6 text-purple-600" />
          </button>
        </div>
        <div class="text-right">
          <p class="text-lg font-semibold text-purple-700">
            {{ t('message.remainingMandala') }}: {{ remainingMandalaDrawings }}
          </p>
        </div>
      </div>

      <div v-show="!phrasesHidden">
        <div v-if="mandalas.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="paid_phrase in mandalas"
            :key="paid_phrase.phrase"
            class="bg-white p-5 rounded-lg shadow-md cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            @click="goToMandala(paid_phrase.phrase)">
            <p class="font-bold text-gray-800">{{ paid_phrase.phrase }}</p>
            <p class="text-sm text-gray-500">{{ new Date(paid_phrase.created_at).toLocaleString() }}</p>
          </div>
        </div>
        <div v-else class="bg-white p-6 rounded-lg shadow-md text-center text-gray-600">
          <p>{{ t('message.noMandalasYet') }}</p>
        </div>
      </div>
      <div v-show="phrasesHidden" class="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
        <p>{{ t('message.phrasesHidden') }}</p>
      </div>

      <div class="mt-8">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">{{ t('message.packages') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DrawingPackage v-for="pkg in drawingPackages" :key="pkg.id" :drawingPackage="pkg" @purchase="purchasePackage" />
        </div>
      </div>

      <div v-if="b64image" class="mt-8 bg-white p-6 rounded-lg shadow-md">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">{{ t('message.yourNewMandala') }}</h2>
        <img :src="'data:image/jpeg;base64,' + b64image" alt="Generated Mandala" class="w-full max-w-full rounded-lg shadow-md mx-auto">
      </div>

      <div v-if="showPaymentConfirmationModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="cancelPayment"></div>
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                  <QuestionMarkCircleIcon class="h-6 w-6 text-purple-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 class="text-base font-semibold leading-6 text-gray-900">
                    {{ t('message.newPhraseConfirmation') }}
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      {{ t('message.newPhraseExplanation') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button type.="button"
                class="inline-flex justify-center rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
                @click="confirmPayment">
                {{ t('message.confirm') }}
              </button>
              <button type="button"
                class="mt-3 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
                @click="cancelPayment">
                {{ t('message.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axiosInstance from '@/axios/axios';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { QuestionMarkCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import DrawingPackage from '@/components/DrawingPackage.vue';

const { t } = useI18n();
const router = useRouter();
const mandalas = ref([]);
const phrase = ref('');
const b64image = ref('');
const showPaymentConfirmationModal = ref(false);
const userInfo = ref(null);
const drawingPackages = ref([]);
const phrasesHidden = ref(localStorage.getItem('phrasesHidden') === 'true');

const togglePhrasesVisibility = () => {
  phrasesHidden.value = !phrasesHidden.value;
  localStorage.setItem('phrasesHidden', phrasesHidden.value);
};

onMounted(async () => {
  try {
    const userInfoResponse = await axiosInstance.get('/api/auth/userinfo/');
    userInfo.value = userInfoResponse.data;

    const paidPhrasesResponse = await axiosInstance.get('/draw/api/v1/premium/paid-phrases/');
    mandalas.value = paidPhrasesResponse.data;

    const drawingPackagesResponse = await axiosInstance.get('/draw/api/v1/free/packages/');
    drawingPackages.value = drawingPackagesResponse.data;

    // Check if user has no drawings remaining and no existing mandalas
    if (userInfo.value?.mandala_drawings?.remaining <= 0 && mandalas.value.length === 0) {
       router.push({ name: 'PaymentView' });
    }

  } catch (error) {
    console.error('Error fetching data:', error);
  }
});

const remainingMandalaDrawings = computed(() => {
  if (userInfo.value && userInfo.value.mandala_drawings) {
    return userInfo.value.mandala_drawings.remaining;
  }
  return null;
});

const goToMandala = (phrase) => {
  router.push({ name: 'app-draw', query: { phrase: phrase } });
};

const drawMandala = async () => {
  if (!phrase.value) {
    alert(t('message.enterPhrase'));
    return;
  }

  try {
    // Check if the phrase is already paid
    await axiosInstance.get('/draw/api/v1/premium/check-phrase/', { params: { phrase: phrase.value } });
    // If it doesn't throw 402, it means it's paid, so we can redirect to the drawing page
    router.push({ name: 'app-draw', query: { phrase: phrase.value } });
  } catch (error) {
    if (error.response && error.response.status === 402) {
      showPaymentConfirmationModal.value = true;
    } else {
      console.error('Error checking phrase:', error);
      alert(t('message.drawError'));
    }
  }
};

const confirmPayment = async () => {
  showPaymentConfirmationModal.value = false;
  try {
    // Pay for the phrase
    await axiosInstance.post('/draw/api/v1/premium/pay-phrase/', { phrase: phrase.value });
    // Redirect to the drawing page
    router.push({ name: 'app-draw', query: { phrase: phrase.value } });
  } catch (payError) {
    console.error('Error paying for phrase:', payError);
    alert(t('message.drawError'));
  }
};

const cancelPayment = () => {
  showPaymentConfirmationModal.value = false;
};

const purchasePackage = async (packageId) => {
  try {
    const response = await axiosInstance.post(`/draw/api/v1/free/packages/purchase/${packageId}/`);
    window.location.href = response.data.payment_link;
  } catch (error) {
    console.error('Error purchasing package:', error);
    alert(t('message.purchaseError'));
  }
};

</script>